import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '@/pages/painel-pedidos/lib/errors';
import { type OportunidadeConsolidada, formatBRL } from '@/pages/painel/lib/comercial';

interface Alerta {
  id: string;
  texto: string;
  tom: 'vermelho' | 'ambar' | 'cinza';
  url: string;
}

interface ProjetoResumo {
  id: number;
  titulo: string;
  valor_potencial: number | null;
  updated_at: string | null;
}

const TOM_STYLE: Record<Alerta['tom'], { box: string; icon: string }> = {
  vermelho: { box: 'border-red-200 bg-red-50/50', icon: 'ri-alarm-warning-line text-red-600' },
  ambar: { box: 'border-amber-200 bg-amber-50/50', icon: 'ri-alert-line text-amber-600' },
  cinza: { box: 'border-gray-200 bg-gray-50/60', icon: 'ri-information-line text-gray-500' },
};

export default function AlertasComerciais({ password }: { password: string }) {
  const [alertas, setAlertas] = useState<Alerta[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [listRes, projRes] = await Promise.all([
        supabase.functions.invoke('comercial-admin', { body: { action: 'listar', password, filtros: { estado: 'abertas' } } }),
        supabase.functions.invoke('comercial-admin', { body: { action: 'projetos', password } }),
      ]);
      if (listRes.error) throw new Error(await extractErrorMessage(listRes.error));
      if (projRes.error) throw new Error(await extractErrorMessage(projRes.error));

      const lista = listRes.data as { oportunidades?: OportunidadeConsolidada[]; error?: string };
      if (lista?.error) throw new Error(lista.error);
      const projetosData = projRes.data as { projetos?: ProjetoResumo[]; error?: string };
      if (projetosData?.error) throw new Error(projetosData.error);

      const abertas = lista?.oportunidades ?? [];
      const acumulados: Alerta[] = [];

      for (const o of abertas) {
        const valorFmt = o.valor != null ? formatBRL(o.valor) : '—';
        if (!o.proxima_acao && (o.valor ?? 0) >= 10000) {
          acumulados.push({
            id: `semacao-${o.id}`,
            texto: `Oportunidade de ${valorFmt} sem próxima ação.`,
            tom: 'vermelho',
            url: '/painel/oportunidades',
          });
        }
        if (o.etapa === 'proposta') {
          acumulados.push({
            id: `proposta-${o.id}`,
            texto: `Proposta de ${valorFmt} sem retorno registrado.`,
            tom: 'ambar',
            url: '/painel/oportunidades',
          });
        }
        if (o.margem_tone === 'vermelha') {
          acumulados.push({
            id: `margem-${o.id}`,
            texto: `Margem abaixo da meta em "${o.titulo}".`,
            tom: 'vermelho',
            url: '/painel/oportunidades',
          });
        } else if (o.margem_tone === 'amarela') {
          acumulados.push({
            id: `margem-${o.id}`,
            texto: `Margem em atenção em "${o.titulo}".`,
            tom: 'ambar',
            url: '/painel/oportunidades',
          });
        }
      }

      for (const p of projetosData?.projetos ?? []) {
        if (p.updated_at) {
          const dias = Math.floor((Date.now() - new Date(p.updated_at).getTime()) / 86400000);
          if (dias >= 15) {
            acumulados.push({
              id: `projeto-${p.id}`,
              texto: `Projeto estratégico "${p.titulo}" sem interação há ${dias} dias.`,
              tom: 'ambar',
              url: '/painel/projetos-estrategicos',
            });
          }
        }
      }

      setAlertas(acumulados.slice(0, 8));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar alertas.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password]);

  return (
    <section className="bg-white rounded-2xl border border-gray-200 p-5">
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-50 text-amber-600">
            <i className="ri-notification-3-line"></i>
          </div>
          <div>
            <h2 className="font-bold text-gray-900">Alertas</h2>
            <p className="text-xs text-gray-500">O que precisa da sua atenção agora.</p>
          </div>
        </div>
        {alertas.length > 0 && (
          <span className="text-xs font-semibold text-amber-700 bg-amber-50 rounded-full px-2.5 py-0.5">{alertas.length}</span>
        )}
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-3">{error}</div>}

      {loading && alertas.length === 0 ? (
        <div className="flex justify-center py-6"><div className="w-7 h-7 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" /></div>
      ) : alertas.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-6">Nenhum alerta no momento. Tudo em ordem.</p>
      ) : (
        <div className="space-y-2">
          {alertas.map((a) => {
            const style = TOM_STYLE[a.tom];
            return (
              <Link
                key={a.id}
                to={a.url}
                className={`flex items-center gap-3 rounded-xl border px-4 py-3 transition-colors cursor-pointer hover:bg-white ${style.box}`}
              >
                <div className="w-8 h-8 shrink-0 flex items-center justify-center rounded-lg bg-white">
                  <i className={style.icon}></i>
                </div>
                <span className="flex-1 text-sm text-gray-800">{a.texto}</span>
                <i className="ri-arrow-right-s-line text-gray-400 shrink-0"></i>
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}